import { useContainerBlockContext, useCustomViewBlockContext } from '@lighthouse/block'
import { SubFormBlock } from '@lighthouse/block/src/blocks'
import type { SubFormColumn } from '@lighthouse/core'
import { type SubFormBlockAbstract } from '@lighthouse/core'
import {
    defaultFileMaxUploadSize,
    FieldBlockProvider,
    type FieldInputError,
    getBlockValueByContext,
    getBlockValueFromBlockMap,
    getFieldInputError,
    getFieldInputRules,
    getWithScopeId,
    setBlockValueAtLeaf,
    useAtomAction,
    useAtomData,
    useFormModuleContext,
    useSpaceQuotaContext
} from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import React, { useCallback, useMemo } from 'react'
import { useAsync, useUpdateEffect } from 'react-use'

import { pageStackOfBlockAtom } from '@/atoms/application/state'
import { recordPoolAtom, viewIndependentDataAtomFamily } from '@/atoms/dataSource/state'
import { pageAtomFamily } from '@/atoms/page/state'
import { useCurrentPageContext, useCurrentStackIdContext } from '@/contexts/PageContext'
import { useCurrentAppId, useCurrentEnvId, useLanguage } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import { useFieldBlockDefaultValueRender } from '@/hooks/useFieldBlockDefaultValueRender'
import { useFieldBlockMethods } from '@/hooks/useFieldBlockMethods'
import * as srv from '@/services'
import { uploadManagerInAppParams } from '@/utils/auth'

interface SubFormBlockControllerProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange'> {
    blockData: SubFormBlockAbstract
    onBlockChange?: (values: SubFormBlockAbstract, origin: SubFormBlockAbstract) => Promise<void> | void
}
const emptyArr: SubFormColumn[] = []

const SubFormBlockController: React.FC<SubFormBlockControllerProps> = ({ blockData, onBlockChange, ...rest }) => {
    const {
        config: { columns = emptyArr, pointer = '' },
        id: blockId
    } = blockData
    const { scope } = useContainerBlockContext()
    const { record: viewRecord } = useCustomViewBlockContext()
    const onVariableValueRender = useFieldBlockDefaultValueRender()
    const { quota } = useSpaceQuotaContext()
    const fileMaxUploadSize = quota?.uploadFileLimitSize ?? defaultFileMaxUploadSize
    const { formId, subFormServiceErrors, updateSubFormErrors, resetSubFormServiceError, submitAttempted, formResetKey } =
        useFormModuleContext()
    const { pageId } = useCurrentPageContext()
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const stackId = useCurrentStackIdContext()
    const dataSource = useDataSource(appId, envId, pointer)
    const language = useLanguage()

    const { run: setPageStackOfBlockAtom } = useAtomAction(pageStackOfBlockAtom)
    const fieldBlockValueMap = useAtomData(
        pageStackOfBlockAtom,
        useCallback(s => s?.[stackId], [stackId])
    )

    const pageName = useAtomData(
        pageAtomFamily(pageId),
        useCallback(s => s?.name ?? '', [])
    )

    const handleFetchSmsCode = useCallback((mobile: string) => srv.getVerifyCode({ mobile, blockId, pageId }), [blockId, pageId])

    /** 获取关联数据源数据 start */
    const { value: initResult } = useAsync(async () => {
        const entries = await Promise.all(
            columns.map(async column => {
                const { config, id: columnId } = column
                const { inputType = 'text', fieldPointer = '', initialValue } = config ?? {}
                const field = dataSource?.schema?.[fieldPointer]
                const initValue = await onVariableValueRender({
                    id: columnId,
                    dsId: pointer,
                    inputType,
                    fieldId: fieldPointer,
                    initialValue,
                    config
                })
                return {
                    columnId,
                    config,
                    inputType,
                    fieldPointer,
                    fieldType: field?.type,
                    value: initValue.value
                }
            })
        )
        return {
            initData: {
                id: nanoid(),
                content: Object.fromEntries(
                    entries.map(e => [
                        e.columnId,
                        {
                            id: blockId,
                            fieldId: e.fieldPointer,
                            type: e.inputType,
                            dsId: pointer,
                            fieldType: e.fieldType,
                            source: 'subForm',
                            value: e.value
                        } as any
                    ])
                )
            },
            relativeDataSourceConfig: entries.map(e => ({
                blockId,
                scope,
                columnId: e.columnId,
                config: e.config,
                value: e.value,
                parentRecordId: viewRecord?.id
            }))
        }
    }, [blockId, columns, dataSource?.schema, onVariableValueRender, pointer, scope, viewRecord?.id])
    const relativeDataSourceConfig = initResult?.relativeDataSourceConfig ?? []
    const { onFetchDataSource, onLoadMoreData, onFetchCascadeOptions, onFetchPersonOptions, onGetInitRecord } = useFieldBlockMethods(
        relativeDataSourceConfig,
        fieldBlockValueMap
    )

    /** 获取关联数据源数据end */

    const uploadyOptions = useMemo(
        () => ({
            // TODO: @kidrue id后续处理掉 不需要此参数
            info: { label: pageName, id: '', groupId: pageId },
            options: {
                ...uploadManagerInAppParams()
            }
        }),
        [pageId, pageName]
    )

    const subFormKey = useMemo(() => getWithScopeId(blockId, scope), [blockId, scope])
    const subFormItem = useMemo(
        () => getBlockValueFromBlockMap(fieldBlockValueMap, { blockId: subFormKey, recordId: viewRecord?.id }),
        [fieldBlockValueMap, subFormKey, viewRecord?.id]
    )
    const dataList = useMemo(() => (subFormItem?.blockType === 'subForm' ? (subFormItem.value as any[]) : []), [subFormItem])
    const errors = useMemo(() => {
        return subFormServiceErrors?.filter(v => v.blockId === blockId) || []
    }, [blockId, subFormServiceErrors])

    const handleChangeSubForm = useCallback(
        (updater: (v: Record<string, any[]>) => void) => {
            setPageStackOfBlockAtom(draft => {
                const existing = getBlockValueByContext(draft, { stackId, blockId: subFormKey, recordId: viewRecord?.id })
                const current = existing?.blockType === 'subForm' ? existing.value : []
                const mapDraft: Record<string, any[]> = { [blockId]: current.map(r => ({ ...r, content: { ...r.content } })) }
                updater(mapDraft)
                const next = mapDraft[blockId] ?? []
                setBlockValueAtLeaf(
                    draft,
                    { stackId, blockId: subFormKey, recordId: viewRecord?.id },
                    { id: blockId, formId, scope, value: next, blockType: 'subForm' }
                )
            })
        },
        [blockId, formId, scope, setPageStackOfBlockAtom, stackId, subFormKey, viewRecord?.id]
    )

    const handleSubFormInputChange = useCallback(
        (_subFormId: string, recordId: string, columnId: string, fieldValue: any) => {
            const column = columns.find(c => c.id === columnId)
            const fieldPointer = column?.config?.fieldPointer ?? ''
            const schemaField = fieldPointer ? dataSource?.schema?.[fieldPointer] : undefined
            resetSubFormServiceError?.(recordId, blockId, columnId)
            const cellError =
                column && schemaField
                    ? getFieldInputError({
                          value: fieldValue,
                          rule: getFieldInputRules({
                              config: { ...(column.config as any), required: false },
                              id: blockId,
                              title: column.config.title ?? ''
                          }),
                          fieldType: schemaField.type
                      })
                    : undefined

            setPageStackOfBlockAtom(draft => {
                const existing = getBlockValueByContext(draft, { stackId, blockId: subFormKey, recordId: viewRecord?.id })
                const current = existing?.blockType === 'subForm' ? (existing.value as any[]) : []
                const rec = current.find(r => r.id === recordId)
                if (!rec) {
                    return
                }
                const nextValue = fieldValue?.value ?? fieldValue
                if (rec.content?.[columnId]) {
                    rec.content[columnId].value = nextValue
                }
                setBlockValueAtLeaf(
                    draft,
                    { stackId, blockId: subFormKey, recordId: viewRecord?.id },
                    { id: blockId, formId, scope, value: current, blockType: 'subForm' }
                )
            })

            if (cellError) {
                updateSubFormErrors?.(draft => {
                    const idx = draft.findIndex(e => e.blockId === blockId && e.recordId === recordId && e.columnId === columnId)
                    if (idx !== -1) {
                        draft.splice(idx, 1)
                    }
                    if (Object.keys(cellError).length > 0) {
                        draft.push({ blockId, recordId, columnId, error: cellError })
                    }
                })
            }
        },
        [
            blockId,
            columns,
            dataSource?.schema,
            formId,
            scope,
            setPageStackOfBlockAtom,
            stackId,
            subFormKey,
            updateSubFormErrors,
            viewRecord?.id,
            resetSubFormServiceError
        ]
    )

    const handleCellValidate = useCallback(
        (reportBlockId: string, recordId: string, columnId: string, error: FieldInputError | undefined) => {
            updateSubFormErrors?.(draft => {
                const idx = draft.findIndex(e => e.blockId === reportBlockId && e.recordId === recordId && e.columnId === columnId)
                if (idx !== -1) {
                    draft.splice(idx, 1)
                }
                if (error && Object.keys(error).length > 0) {
                    draft.push({ blockId: reportBlockId, recordId, columnId, error })
                }
            })
        },
        [updateSubFormErrors]
    )

    useUpdateEffect(() => {
        setPageStackOfBlockAtom(draft => {
            setBlockValueAtLeaf(
                draft,
                { stackId, blockId: subFormKey, recordId: viewRecord?.id },
                { id: blockId, formId, scope, value: [], blockType: 'subForm' }
            )
        })
        updateSubFormErrors?.(draft => {
            for (let i = draft.length - 1; i >= 0; i--) {
                if (draft[i].blockId === blockId) {
                    draft.splice(i, 1)
                }
            }
        })
    }, [formResetKey])

    return (
        <FieldBlockProvider
            appId={appId}
            stackId={stackId}
            recordPoolAtom={recordPoolAtom}
            viewIndependentDataAtomFamily={viewIndependentDataAtomFamily}
        >
            <SubFormBlock
                scope={scope}
                viewRecordId={viewRecord?.id}
                appId={appId}
                envId={envId}
                dataList={dataList}
                initData={initResult?.initData}
                isValid={false}
                blockData={blockData}
                dataSource={dataSource}
                dataSourceList={dataSourceList}
                language={language}
                errors={errors}
                uploadyOptions={uploadyOptions}
                fileMaxUploadSizeBytes={fileMaxUploadSize}
                richTextUploadOptions={uploadManagerInAppParams()}
                onChange={handleChangeSubForm}
                onBlockChange={onBlockChange}
                onVariableValueRender={onVariableValueRender}
                submitAttempted={submitAttempted}
                onInputChange={handleSubFormInputChange}
                onCellValidate={handleCellValidate}
                onFetchSmsCode={handleFetchSmsCode}
                onFetchCascadeOptions={onFetchCascadeOptions}
                onFetchDataSource={onFetchDataSource}
                onLoadMoreData={onLoadMoreData}
                onFetchPersonOptions={onFetchPersonOptions}
                onGetInitRecord={onGetInitRecord}
                {...rest}
            />
        </FieldBlockProvider>
    )
}

export default SubFormBlockController
